import { jwtDecode } from "jwt-decode";

export const AUTH_TOKEN_STORAGE_KEY = "access";

// localStorage (not sessionStorage): the token must be visible to every tab
// of the app, not just the one that received it -- opening any PlanMat link
// in a new tab (Ctrl+click, a plain window.open, pasting the URL, or one
// left over after closing every other tab) has to just work as long as the
// session is still valid. Wrapped in try/catch for the same reason
// post-login-redirect.ts is: private browsing / storage quota can make
// localStorage throw on write.
export const authTokenStorage = {
  get(): string | null {
    try {
      return localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    } catch {
      return null;
    }
  },

  set(token: string): void {
    try {
      localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
    } catch {
      // best effort -- this tab keeps working in memory via React state,
      // it just won't survive a reload or be visible to other tabs.
    }
  },

  clear(): void {
    try {
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    } catch {
      // no-op
    }
  },
};

/** A token past its `exp` is worse than no token: it would render the app
 * authenticated for one paint and then fail the first request with a 401.
 * Checked wherever a token is adopted without having just come from a fresh
 * login/handshake (i.e. wherever it could be stale). */
export const isTokenExpired = (token: string): boolean => {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    return !!exp && exp * 1000 <= Date.now();
  } catch {
    return true; // undecodable == unusable
  }
};
